import { FunctionObj } from '../../vo/workingdata/function-obj';
import { CollectionFunctions } from './funcs/collection.funcs';
import { DateFunctions } from './funcs/date.funcs';
import { LogicalFunctions } from './funcs/logical.funcs';
import { MathFunctions } from './funcs/math.funcs';
import { RefFunctions } from './funcs/ref.funcs';
import { TextFunctions } from './funcs/text.funcs';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { WidgetLine } from '../../vo/workingdata/widget-line.obj';
import { MeasModeItem } from './measure.mode.obj';
import { WizConstants } from '../../model/constants';
import { Model } from '../../model/model';

/**
 * fonctions de base, statiques, pour calculs (indépendant de la vision)
 */
export class FunctionCoreEngine {
  // constantes de traitement
  /** référence sur données externes (autres visions) */
  public static readonly CROSSED_KEY: string = 'CROSSED';
  /** Collection sur une mesure de type liste */
  public static readonly COLLECT_LIST_KEY: string = 'COLLECTLIST';
  /** Collection */
  public static readonly COLLECT_KEY: string = 'COLLECT';
  /** Collection avec level cible */
  public static readonly SUBCOLLECT_KEY: string = 'SUBCOLLECT';
  /** Collection de même niveau, mes soeurs (même parent) */
  public static readonly SISTERS_KEY: string = 'SISTERS';
  /** Collection de même niveau, mes soeurs (même structure de clé) */
  public static readonly GROUPVAL_KEY: string = 'GROUPVAL';
  /** Collection de même niveau, tout le monde */
  public static readonly FOLKS_KEY: string = 'FOLKS';
  /** Collection slide */
  public static readonly SLIDEAGG_KEY: string = 'SLIDEAGG';
  /** Collection slideFrom */
  public static readonly FROMAGG_KEY: string = 'FROMAGG';
  /** Répartition */
  public static readonly SPLIT_KEY: string = 'SPLIT';
  /** Répartition par héritage */
  public static readonly INHERIT_KEY: string = 'INHERIT';
  /** Répartition par héritage sur ancêtre */
  public static readonly ANCESTOR_KEY: string = 'ANCESTOR';
  /** Pas de limitation */
  public static readonly UNLIMITED: string = 'UNLIMITED';
  /** Fonction non applicable */
  public static readonly FUNCTION_NOT_APPLICABLE: string = '#NA';
  /** Erreur de fonction */
  public static readonly FUNCTION_ERROR: string = '#ERR';
  /** Erreur de fonction: fonction inconnue */
  public static readonly UNKNOWN_FUNCTION_ERROR: string = '#FORMULA_ERR';
  /** Erreur de fonction: paramètre vide */
  public static readonly EMPTY_FUNCTION_PARAM_ERROR: string = '#FCT_EMPTY_ERR';
  /** Erreur de fonction: manque un paramètre ou trop de paramètres */
  public static readonly MISMATCH_FUNCTION_PARAM_ERROR: string = '#FCT_MIS_ERR';
  /** Erreur de doublon d'opérateur */
  public static readonly OPERATOR_ERROR: string = '#OP_ERR';
  /** Erreur de doublon de valeur */
  public static readonly VALUE_ERROR: string = '#VAL_ERR';
  /** référence circulaire */
  public static readonly CIRCULAR_ERROR: string = '#CIRC_ERR';
  /** mode inconnu */
  public static readonly UNKNOW_MODE_ERROR: string = '#MOD_ERR';
  /** mesure inconnue */
  public static readonly UNKNOW_MEASURE_ERROR: string = '#MES_ERR';
  /** élément inconnu */
  public static readonly UNKNOW_PART_ERROR: string = '#PART_ERR';
  /** élément inconnu */
  public static readonly EXTRA_SPACE_ERROR: string = '#SPACE_ERR';
  /** séparateur des paramètres de fonctions */
  public static readonly FUNCTION_SEPARATOR: string = ';';

  /** référence de clé */
  public static readonly KEYVAL_KEY: string = 'KEYVAL';
  /** référence à une valeur parent */
  public static readonly PARENTKEYVAL_KEY: string = 'PARENTKEYVAL';
  /** référence à une valeur grand-parent */
  public static readonly ANCESTORKEYVAL_KEY: string = 'ANCESTORKEYVAL';
  /** référence directe de FDV */
  public static readonly FDVAL_KEY: string = 'FDVAL';

  public static readonly ZODIAC_KEY: string = 'ZODIAC';
  public static readonly CHINESEZODIAC_KEY: string = 'CHINESEZODIAC';

  /** Collection avec level cible */
  public static readonly LIST_KEY: string = 'LIST';


  /** Map des fonctions map[code de la fonction] = [description, nb paramètres] */
  private static _functionMap: Map<string, FunctionObj> = null;

  /**
   * Initialisation de la map des fonctions
   *
   * @return Map<String, String> Map des fonctions alimentée
   */
  public static functionMap(model: Model): Map<string, FunctionObj> {
    if (this._functionMap === null) {
      let funcArr: FunctionObj[] = [];

      funcArr = funcArr.concat(MathFunctions.functions(model)); // fonctions maths
      funcArr = funcArr.concat(TextFunctions.functions(model)); // fonctions textes
      funcArr = funcArr.concat(DateFunctions.functions(model)); // fonctions dates
      funcArr = funcArr.concat(LogicalFunctions.functions(model)); // fonctions logiques
      funcArr = funcArr.concat(RefFunctions.functions(model)); // fonctions référentiel
      funcArr = funcArr.concat(CollectionFunctions.functions(model)); // fonctions collections


      this._functionMap = new Map(funcArr.map((item) => [item.code, item]));
    }
    return this._functionMap;
  }

  /** set des fonctions traductibles */
  private static _translatableFunctionSet: Set<string> = null;

  /**
   * Initialisation du set
   * @return _translatableFunctionSet
   */
  public static translatableFunctionSet(): Set<string> {
    if (this._translatableFunctionSet === null) {
      let funcSet: Set<string> = new Set([
        this.KEYVAL_KEY, 
        this.PARENTKEYVAL_KEY, 
        this.ANCESTORKEYVAL_KEY, 
        this.FDVAL_KEY, 
        this.ZODIAC_KEY, 
        this.CHINESEZODIAC_KEY
      ]);
      this._translatableFunctionSet = funcSet;
    }
    return this._translatableFunctionSet;
  }

  /**
   * mappeur de fonction par code
   * TODO ajouter les fonctions du user ?
   *
   * @param functionName Nom de la fonction
   * @param mmi          Etape de calcul concernée
   * @param line         WidgetLine de travail
   * @param fei          Moteur de calcul
   * @param fixedMmi     collection de lignes enfants déjà calculées (pour les sisters / les séries)
   */
  public static callFunction(functionName: string, mmi: MeasModeItem, line: WidgetLine, fei: FeiVO, fixedMmi?: string[]) {

    let startTime: number = 0; // début mesure
    let endTime: number = 0; // fin de mesure
    if (this.trackFunctionTime) {
      startTime = new Date().getTime();
    }

    switch (functionName) {

      case 'ABS':
        MathFunctions.ABS(mmi);
        break;
      case 'ROUND':
        MathFunctions.ROUND(mmi);
        break;
      case 'ROUNDDOWN':
        MathFunctions.ROUND(mmi, MathFunctions.FLOOR);
        break;
      case 'ROUNDUP':
        MathFunctions.ROUND(mmi, MathFunctions.CEIL);
        break;
      case 'SUM': // fonction autorisée avec COLLECT
        MathFunctions.SUM(mmi, fixedMmi, fei);
        break;
      case 'AVG': // fonction autorisée avec COLLECT
        MathFunctions.AVG(mmi, fixedMmi, fei);
        break;
      case 'MAX': // retourne le Max de la collection
        MathFunctions.MAX(mmi, fixedMmi, fei);
        break;
      case 'MIN': // retourne le Min de la collection
        MathFunctions.MIN(mmi, fixedMmi, fei);
        break;
      case 'POW': // fonction puissance
        MathFunctions.POW(mmi);
        break;
      case 'COUNT': // fonction numération
        MathFunctions.COUNT(mmi, fixedMmi, fei);
        break;
      case 'COUNTDISTINCT': // numération des valeurs distinctes
        MathFunctions.COUNTDISTINCT(mmi, fixedMmi, fei);
        break;
      case 'RANDOM': // Alea
        MathFunctions.RANDOM(mmi);
        break;
      case 'COS': // cosinus
        MathFunctions.COS(mmi);
        break;
      case 'SIN': // sinus
        MathFunctions.SIN(mmi);
        break;
      case 'TAN': // tangente
        MathFunctions.TAN(mmi);
        break;
      case 'LOG': // logarithme
        MathFunctions.LOG(mmi);
        break;
      case 'EXP': // exponentiel
        MathFunctions.EXP(mmi);
        break;
      case 'SQRT': // racine carrée
        MathFunctions.SQRT(mmi);
        break;
      case 'PI': // pi
        MathFunctions.PI(mmi);
        break;
      case 'E': // E
        MathFunctions.E(mmi);
        break;
      case 'DIVIDE':
        MathFunctions.DIVIDE(mmi);
        break;
      case 'ENUMERATE': // compte TOUS les sous-éléments (même les vides)
        MathFunctions.ENUMERATE(mmi, fixedMmi, fei);
        break;
      case 'GPSDIST': // distance GPS entre 2 coordonnées
        MathFunctions.GPSDIST(mmi);
        break;



      case 'CONCAT': // fonction concatenation
        TextFunctions.CONCAT(mmi);
        break;
      case 'FROMPERCENT':
        TextFunctions.FROMPERCENT(mmi);
        break;
      case 'NBCHAR':
        TextFunctions.NBCHAR(mmi);
        break;
      case 'LEFT':
        TextFunctions.LEFT(mmi);
        break;
      case 'RIGHT':
        TextFunctions.RIGHT(mmi);
        break;
      case 'REPLACE':
        TextFunctions.REPLACE(mmi);
        break;
      case 'SUBSTR':
        TextFunctions.SUBSTR(mmi);
        break;
      case 'TRIM':
        TextFunctions.TRIM(mmi);
        break;
      case 'JOIN': // fonction concatenation avec séparateur
        TextFunctions.JOIN(mmi);
        break;
      case 'REGEX': // application REGEX sur le texte (injection de caractères spéciaux \", par exemple)
        TextFunctions.REGEX(mmi);
        break;
      case 'UPPER': // forcer majuscule
        TextFunctions.UPPER(mmi);
        break;
      case 'LOWER': // forcer minuscule
        TextFunctions.LOWER(mmi);
        break;
      case 'PADLEFT':
        TextFunctions.PADLEFT(mmi);
        break;
      case 'PADRIGHT':
        TextFunctions.PADRIGHT(mmi);
        break;


      case 'DATEADD': // ajoute année, mois, jour à une date
        DateFunctions.DATEADD(mmi);
        break;
      case 'DATEDIFF': // écart en jours entre 2 dates
        DateFunctions.DATEDIFF(mmi);
        break;
      case 'TODAY': // date du jour
        DateFunctions.TODAY(mmi);
        break;
      case 'TODATE': // crée une date à partir de Année / mois / jour
        DateFunctions.TODATE(mmi);
        break;
      case 'FORCEDAY': // force le jour sur une date
        DateFunctions.FORCEDAY(mmi);
        break;
      case 'YEAR': // retourne YYYY d'une date
        DateFunctions.YEAR(mmi);
        break;
      case 'MONTH': // retourne MM d'une date
        DateFunctions.MONTH(mmi);
        break;
      case 'DAY': // retourne dd d'une date
        DateFunctions.DAY(mmi);
        break;
      case 'WEEK': // retourne le num semaine d'une date
        DateFunctions.WEEK(mmi);
        break;
      case 'CHINESEZODIAC': // retourne le signe astrologique chinois
        DateFunctions.CHINESEZODIAC(mmi, fei);
        break;
      case 'ZODIAC': // retourne le signe astrologique
        DateFunctions.ZODIAC(mmi, fei);
        break;
      case 'NOW': // date du jour avec heure min sec
        DateFunctions.NOW(mmi);
        break;
      case 'TIMEADD': // ajout de temps sur une heure
        DateFunctions.TIMEADD(mmi);
        break;
      case 'TIMEDIFF': // écart en secondes entre 2 heures
        DateFunctions.TIMEDIFF(mmi);
        break;
      case 'FULLTIMEAGG': // fusionne une date et une heure
        DateFunctions.FULLTIMEAGG(mmi);
        break;
      case 'FULLTIMEADD': // ajout de temps à un fulltime
        DateFunctions.FULLTIMEADD(mmi);
        break;
      case 'FULLTIMEDIFF': // delta en s, m, h ou d entre 2 dates
        DateFunctions.FULLTIMEDIFF(mmi);
        break;
      case 'TARGETDAY': // retourne le jour de semaine proche de la date
        DateFunctions.TARGETDAY(mmi);
        break;

      case 'IF': // test logique sur premier élément (1/0) donne résult 2 ou 3
        LogicalFunctions.IF(mmi);
        break;
      case 'IFS': // collection de couples test logique / result
        LogicalFunctions.IFS(mmi);
        break;       
      case 'OR': // test logique true si à minima 1 true
        LogicalFunctions.OR(mmi, fei);
        break;
      case 'AND': // test logique true si all.true
        LogicalFunctions.AND(mmi, fei);
        break;
      case 'NOT': // inverse un boolean
        LogicalFunctions.NOT(mmi);
        break;
      case 'PREFER': // prend le 1er si existe, sinon le deuxième
        LogicalFunctions.PREFER(mmi, fixedMmi, fei);
        break;
      case 'MANDATORY': // rend le champ obligatoire (restrict)
        LogicalFunctions.MANDATORY(mmi);
        break;
      case 'UNIQUE': // rend le champ unique (restrict)
        LogicalFunctions.UNIQUE(mmi, line, fei);
        break;
      case 'EMPTY': // retourne true si le champ est null / vide
        LogicalFunctions.EMPTY(mmi);
        break;
      case 'REQUIRED': // tout les params non nuls = true
        LogicalFunctions.REQUIRED(mmi, null, fei);
        break;
      case 'REQUIREDFILTERS': // tout les params non nuls = true
        LogicalFunctions.REQUIREDFILTERS(mmi, line, fei);
        break;
      case 'COMPLETION': // tout les params non nuls = score sur 100%
        LogicalFunctions.COMPLETION(mmi, line, fei);
        break;


      case 'WHO': // retourne l'identifiant du user connecté
        RefFunctions.WHO(mmi, line, fei);
        break;
      case 'KEYVAL': // retourne un sous-champ d'un FD de la clé / filterKey
        RefFunctions.KEYVAL(mmi, line, fei);
        break;
      case 'PARENTKEYVAL': // retourne un sous-champ d'un FD de la clé / filterKey de la baseline PARENTE
        RefFunctions.PARENTKEYVAL(mmi, line, fei);
        break;
      case 'ANCESTORKEYVAL': // retourne un sous-champ d'un FD de la clé / filterKey de la baseline ANCETRE
        RefFunctions.ANCESTORKEYVAL(mmi, line, fei);
        break;
      case 'FDVAL': // retourne une valeur de FDV
        RefFunctions.FDVAL(mmi, line, fei);
        break;
      case 'KEYLIST': // collecte des sous-valeurs d'une codif et choix des clés
        RefFunctions.KEYLIST(mmi, line, fei);
        break;
      case 'TRIGGER': // déclenche la mise à jour et la persistence de la valeur param1 depuis le calcul param2
        RefFunctions.TRIGGER(mmi, line, fei);
        break;
      case 'CROSSED': // recherche d'une valeur sur une autre vision
        RefFunctions.CROSSED(mmi, line, fei);
        break;
      case 'THRESHOLD': // comparaison sur la base d'une codif de seuil
        RefFunctions.THRESHOLD(mmi, line, fei);
        break;
      case 'LISTITEMVAL': // collecte la valeur d'un élément de la liste
        RefFunctions.LISTITEMVAL(mmi);
        break;
      case 'USERKEYVAL': // retourne une valeur pour un axe utilisateur (sur role / profil)
        RefFunctions.USERKEYVAL(mmi, line, fei);
        break;
      case 'INJECT': // injection d'un champ dans un autre
        RefFunctions.INJECT(mmi, line, fei);
        break;        
      case 'WLKEY': // collecte de la clé de Widgetline
        RefFunctions.WLKEY(mmi, line, fei);
        break;   
      case 'WORKCOMVAL': // retourne une valeur de workflow
        RefFunctions.WORKCOMVAL(mmi, line, fei);
        break; 

      case this.SPLIT_KEY: // ventilation avec poids
        CollectionFunctions.SPLIT(mmi, line, fei);
        break;
      case this.INHERIT_KEY: // ventilation à l'identique
        CollectionFunctions.INHERIT(mmi, line, fei);
        break;
      case 'LASTVALUEOF': // collecte mes soeurs de clé (et moi) sur un chrono / linéaire, et renvoi la plus haute mesure
        CollectionFunctions.LASTVALUEOF(mmi, line, fei);
        break;
      case 'LASTINDEXOF': // collecte mes soeurs de clé (et moi) sur un chrono / linéaire, et renvoi la valeur du chrono
        CollectionFunctions.LASTINDEXOF(mmi, line, fei);
        break;
      case this.FROMAGG_KEY: // collecte mes soeurs de clé (et moi) sur un chrono / linéaire, et renvoi la valeur du chrono
        CollectionFunctions.FROMAGG(mmi, line, fei);
        break;
      case this.COLLECT_LIST_KEY: // aggrégat de valeurs d'une mesure de type liste
      CollectionFunctions.COLLECTLIST(mmi, line, fei);
        break;  
      case this.COLLECT_KEY: // aggrégat de valeurs d'enfants
        CollectionFunctions.COLLECT(mmi, line, fei);
        break;
      case this.SUBCOLLECT_KEY: // collecte avec niveau de profondeur
        CollectionFunctions.SUBCOLLECT(mmi, line, fei);
        break;
      case 'SERIES': // fonction series : suites
        CollectionFunctions.SERIES(mmi, line, fei);
        break;
      case this.SLIDEAGG_KEY: // fonction series : suites
        CollectionFunctions.SLIDEAGG(mmi, line, fei);
        break;
      case this.ANCESTOR_KEY: // ventilation à l'identique depuis un level spécifique
        CollectionFunctions.ANCESTOR(mmi, line, fei);
        break;
      case 'FIRST': // prend le premier élément d'une série
        CollectionFunctions.FIRST(mmi, fei);
        break;
      case 'LAST': // prend le dernier élément d'une série
        CollectionFunctions.LAST(mmi, fei);
        break;
      case 'LIST': // affiche la liste des sous-éléments
        CollectionFunctions.LIST(mmi, line, fei);
        break;
      case 'COMPARERANGE': // comparaison des objets sur une plage de date. True si > 1
        CollectionFunctions.COMPARERANGE(mmi, line, fei);
        break;
      case 'FALSETWINS': // comparaison des objets sur une plage de date. True si # valeurs pour un même pool
        CollectionFunctions.FALSETWINS(mmi, line, fei);
        break;
      case this.SISTERS_KEY: // aggrégat de valeurs de même niveau, juste mes soeurs (même parent)
        CollectionFunctions.SISTERS(mmi, line, fei);
        break;
      case this.FOLKS_KEY: // aggrégat de valeurs de même niveau
        CollectionFunctions.FOLKS(mmi, line, fei);
        break;
      case this.GROUPVAL_KEY: // agrégat pour des éléments de même clé
        CollectionFunctions.GROUPVAL(mmi, line, fei);
        break;

    }

    if (this.trackFunctionTime && !!fei) {
      endTime = new Date().getTime();
      if(!this._byStepFunctionTime.has(fei.uniqueName)) {
        this._byStepFunctionTime.set(fei.uniqueName, new Map());
      }

      let previousTime = this._byStepFunctionTime.get(fei.uniqueName).get(functionName) || 0;
      previousTime += (endTime - startTime);
      this._byStepFunctionTime.get(fei.uniqueName).set(functionName, previousTime);
    }

  }

  /** mode temps de réponse actif */
  public static trackFunctionTime: boolean = false;

  /** temps cumulés par fonction pour chaque step */
  private static _byStepFunctionTime: Map<any, Map<string, number>> = null;

  /** reset de la map des temps par fonctions */
  public static startFunctionTime(): void {
    this.trackFunctionTime = true;
    this._byStepFunctionTime = new Map();
  }

  /** fin de mesure des temps par fonction, retourne la map */
  public static stopFunctionTime(): void {
    this.trackFunctionTime = false;

    [...this._byStepFunctionTime.keys()].forEach((feiKey: string) => {
      console.log('##################');
      console.log('###### Step: ', feiKey);
      console.log('##################');

      [...this._byStepFunctionTime.get(feiKey).keys()].forEach((fctKey: string) => {
        console.log(fctKey, ' : ', this._byStepFunctionTime.get(feiKey).get(fctKey), ' ms');
      });
    });
  }

  /**
   * contrôle le mmi pour vérifier que la fonction existe
   * La marque en erreur dans le cas contraire
   * @param mmi
   * @returns true si existe
   */
  public static checkFunctionExist(model: Model, mmi: MeasModeItem): boolean {
    const func: FunctionObj = this.functionMap(model).get(mmi.funcCode);
    if (!func) {
      console.error('WrongFunction ', mmi.funcCode); // la fonction n'existe pas
      mmi.isErr = true;
      mmi.r = FunctionCoreEngine.UNKNOWN_FUNCTION_ERROR;
      mmi.errC = FunctionCoreEngine.FUNCTION_ERROR;

      // et on marque aussi le measureMode
      mmi.mm.isErr = true;
      mmi.mm.errC = FunctionCoreEngine.UNKNOWN_FUNCTION_ERROR;
      mmi.mm.errS = mmi.form;
    } 
    return !!func;
  }

  /**
   * marquer le caractère traductible de la mesure
   * @param mmi
   * @returns true si existe
   */
  public static checkTranslatableFunction(model: Model, mmi: MeasModeItem, params: string[]): void {
    
    const func: FunctionObj = this.functionMap(model).get(mmi.funcCode);
    if(this.translatableFunctionSet().has(func.code)) {

      // prise de la clé selon le paramètre (et selon la fonction)
      let trsKey: string = null;

      switch(func.code) {
        case this.KEYVAL_KEY:
        case this.PARENTKEYVAL_KEY:
        case this.ANCESTORKEYVAL_KEY:
          trsKey = params[1]?.replace(new RegExp('"', 'g'), '');
          break;
        case this.FDVAL_KEY:
          trsKey = params[1]?.replace(new RegExp('"', 'g'), '');
          break;
        case this.COLLECT_LIST_KEY:
        case this.ZODIAC_KEY:
        case this.CHINESEZODIAC_KEY:
          trsKey = 'label'; // sont toujours traductibles, ces 2 là
          break;
      }

      if(trsKey === 'label' || trsKey === 'description') {
        // console.log('translatableFunctionSet', mmi.mm.m.shortCode, mmi.funcCode);
        mmi.mm.useLocale = true;
      }
    }
  }


  /**
   * contrôle le mmi pour vérifier que les paramètres de la fonction sont corrects
   * La marque dans le cas contraire
   * @param mmi
   * @param params: string[] les paramètres en cours de création
   * @returns true si conforme
   */
  public static checkFunctionParameters(model: Model, mmi: MeasModeItem, params: string[]): boolean {
    const func: FunctionObj = this.functionMap(model).get(mmi.funcCode);
    const result: boolean = func.checkParamsLength(params.length);
    if (!result) {
      mmi.isErr = true;
      mmi.r = FunctionCoreEngine.MISMATCH_FUNCTION_PARAM_ERROR;
      mmi.errC = FunctionCoreEngine.MISMATCH_FUNCTION_PARAM_ERROR;

      mmi.mm.isErr = true;
      mmi.mm.errC = FunctionCoreEngine.MISMATCH_FUNCTION_PARAM_ERROR;
      mmi.mm.errS = params.length + '/' + func.parameters.length;
    }
    return result;
  }


  /**
   * contrôle le mmi pour identifier les fonctions d'aggrégat
   * @param model 
   * @param code de la fonction
   */
  public static checkIsCollectionFunction(model: Model, code: string): boolean {
    return this.functionMap(model)?.get(code)?.isAggregate;
  }

  /**
   * contrôle le mmi pour identifier les fonctions de type listes
   * @param model 
   * @param code de la fonction
   * @returns 
   */
  public static checkIsListFunction(model: Model, code: string): boolean {
    return this.functionMap(model)?.get(code)?.isList;
  }

  /**
   * recherche de dépendances sur des visions externes (fonction CROSSED)
   * @param fei 
   * @param mmi 
   * @param params 
   */
  public static getCrossDependencies(fei: FeiVO, mmi: MeasModeItem, params: string[]): void {
    switch (mmi.funcCode) {
      case FunctionCoreEngine.CROSSED_KEY:
        const visionCode: string = params[0].replace(new RegExp('"', 'g'), '');
        const stepLevel: number = Number(params[1]);
        if(!mmi.mm.crDeps) {
          mmi.mm.crDeps = new Set<string>();
        }
        mmi.mm.crDeps.add([visionCode, stepLevel].join(WizConstants.CONTEXT_SEPARATOR));
        // console.log('mmi', mmi, params);
      break;
    }
  }

  /**
   * va alimenter les éventuels niveau de dépendance sur le MeasureMode
   * @param fei porteur
   * @param mmi la fonction
   */
  public static getStepLevelDependencies(fei: FeiVO, mmi: MeasModeItem, params: string[]): void {

    let targetLevel: number = null;

    switch (mmi.funcCode) {
      case FunctionCoreEngine.INHERIT_KEY:
      case FunctionCoreEngine.SPLIT_KEY:
      case FunctionCoreEngine.PARENTKEYVAL_KEY:
        targetLevel = fei.visionStepLevel - 1;
        break;
      case FunctionCoreEngine.COLLECT_KEY:
      case FunctionCoreEngine.LIST_KEY:
        targetLevel = fei.visionStepLevel + 1;
        break;
      case FunctionCoreEngine.ANCESTORKEYVAL_KEY:
      case FunctionCoreEngine.ANCESTOR_KEY:
        targetLevel = Number(params[1]);
        break;
      case FunctionCoreEngine.SUBCOLLECT_KEY:
        targetLevel = Number(params[1]);
        break;
    }

    if (!!targetLevel) {
      if (!mmi.mm.stepDeps) {
        mmi.mm.stepDeps = new Set();
      }
      mmi.mm.stepDeps.add(targetLevel);
    }
  }

  /**
   * contrôle le MMI pour les fonctions d'aggrégat. Va transformer le COLLECT en collection de content (si nécessaire)
   *
   * @param mmi Etape de calcul concernée
   * @return MeasModeItem un clone du Mmi, content() toujours alimenté
   */
  public static collectToMmiContent(model:Model, mmi: MeasModeItem): string[] {

    let result: string[] = [];
    // on teste l'enfant 0 de la fonction (pour les aggrégat)
    const childZero: MeasModeItem = mmi?.c[0]?.c[0]; // rien dans une fonction, c''est l'étape ()

    if (this.checkIsCollectionFunction(model, childZero.funcCode) || this.checkIsListFunction(model, childZero.funcCode)) {
      result = childZero.cc || [];
    } else { // un mmi simple (pas d'aggrégat)
      mmi.c.forEach((subMMi: MeasModeItem) => {
        if(subMMi.r !== undefined) {
          result.push(subMMi.r);
        } else {
          result.push(null);
        }
      });
    }
    return result;
  }

}
